import { useGLTF, ContactShadows } from '@react-three/drei'
import { useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import CameraController from './CameraController'
import POIMarkers from './POIMarkers'
import POIColliders from './POIColliders'
import Decals from './Decals'
import DecalPlacer from './DecalPlacer'
import { useDevModeStore } from '../stores/useDevModeStore'
import { useMaterialStore } from '../stores/useMaterialStore'
import { useEnergyFlowStore } from '../stores/useEnergyFlowStore'
import { useLoadingStore } from '../stores/useLoadingStore'
import { useDoorInteraction } from '../hooks/useDoorInteraction'
import { useEnergyFlowAnimation } from '../hooks/useEnergyFlowAnimation'
import { useMeshHighlight } from '../hooks/useMeshHighlight'

export default function Scene() {
  const { scene } = useGLTF('/models/Blockout.glb', true)
  const directionalLightRef = useRef<THREE.DirectionalLight>(null)
  const hasSignaledLoadRef = useRef(false)
  const frameCountRef = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textMeshesRef = useRef<THREE.Mesh[]>([])
  const { setProgress, setLoaded, isLoaded } = useLoadingStore()

  // Get values from dev mode store
  const {
    ambientIntensity,
    directionalIntensity,
    directionalAzimuth,
    directionalElevation,
    hemisphereIntensity,
    hemisphereSkyColor,
    hemisphereGroundColor,
    shadowType,
    shadowOpacity,
    shadowBlur,
    shadowResolution,
    shadowMapSize,
    shadowBias,
    shadowNormalBias,
    shadowCameraSize,
    shadowRadius,
  } = useDevModeStore()

  // Scene center for shadow camera target
  const sceneCenter = { x: 0, y: 0, z: 0 }

  // Convert azimuth/elevation to directional light position
  const getDirectionalLightPosition = (): [number, number, number] => {
    const distance = shadowCameraSize // Keep light far enough for shadow frustum to cover scene at all angles
    const azimuthRad = (directionalAzimuth * Math.PI) / 180
    const elevationRad = (directionalElevation * Math.PI) / 180

    const x = distance * Math.cos(elevationRad) * Math.sin(azimuthRad)
    const y = distance * Math.sin(elevationRad)
    const z = distance * Math.cos(elevationRad) * Math.cos(azimuthRad)

    return [x, y, z]
  }

  // Persist mesh -> material name mapping for reactive variation updates
  const meshMaterialMapRef = useRef<Map<string, string>>(new Map())

  // Get material store functions
  const {
    initializeMaterials,
    getThreeMaterial,
    getMaterial,
    createVariedClone,
    loadDefaultMaterials,
  } = useMaterialStore()

  // Subscribe to materials map for reactive variation updates
  const materials = useMaterialStore((state) => state.materials)

  // POI-triggered door open/close
  useDoorInteraction(scene)

  // Animated energy flow on pipe meshes (Wires material)
  useEnergyFlowAnimation()

  // POI hover mesh highlighting
  useMeshHighlight()

  useEffect(() => {
    const loadAndApplyMaterials = async () => {
      // Fetch default materials and server config in parallel
      const energyFlowPromise = useEnergyFlowStore.getState().loadFromServer()
      const [, serverConfigText] = await Promise.all([
        loadDefaultMaterials(),
        fetch('/data/material-config.json')
          .then((res) => (res.ok ? res.text() : null))
          .catch(() => null),
      ])

      // Collect all unique materials from the GLB
      const materialNames: string[] = []
      const materialMap = new Map<string, string>() // mesh -> material name

      // First pass: collect all unique material names and create mapping
      scene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          const processMaterial = (mat: THREE.Material) => {
            const matName = mat.name || `Material_${materialNames.length + 1}`
            if (!materialNames.includes(matName)) {
              materialNames.push(matName)
            }
            return matName
          }

          if (Array.isArray(child.material)) {
            // Handle multi-material meshes
            const matNames = child.material.map((mat) => processMaterial(mat))
            materialMap.set(child.uuid, matNames.join('|')) // Store multiple materials
          } else {
            const matName = processMaterial(child.material)
            materialMap.set(child.uuid, matName)
          }
        }
      })

      console.log('=== MATERIALS FOUND IN MODEL ===')
      materialNames.forEach((name, idx) => {
        console.log(`${idx + 1}. "${name}"`)
      })
      console.log(`Total unique materials: ${materialNames.length}`)

      // Determine if server has valid material config
      let hasServerConfig = false
      if (serverConfigText) {
        try {
          const parsed = JSON.parse(serverConfigText)
          hasServerConfig = !!parsed.materials
        } catch {
          // invalid JSON, fall through to defaults
        }
      }

      if (hasServerConfig) {
        // Server config exists: init without textures, then import server config
        // which loads textures exactly once via updateMaterial
        try {
          initializeMaterials(materialNames, true)
          useMaterialStore.getState().importFromJSON(serverConfigText!)
          useMaterialStore.setState({ isDirty: false })
        } catch {
          // Fallback: re-init with default textures if server config import fails
          initializeMaterials(materialNames)
        }
      } else {
        // No server config: init with textures from defaults
        initializeMaterials(materialNames)
      }

      // Wait for energy flow config (started in parallel above)
      await energyFlowPromise

      // Persist the mapping for reactive variation updates
      meshMaterialMapRef.current = materialMap

      // Second pass: apply materials from store and configure shadows
      scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Enable shadows for ALL meshes (text meshes don't cast shadows)
          const isTextMesh = child.name?.toLowerCase().startsWith('text') ?? false
          child.castShadow = !isTextMesh
          child.receiveShadow = true

          // Track text meshes for ContactShadows exclusion
          if (isTextMesh) {
            textMeshesRef.current.push(child)
          }

          // Apply materials only if mapping exists
          const matMapping = materialMap.get(child.uuid)
          if (!matMapping) return

          if (matMapping.includes('|')) {
            // Multi-material mesh (skip variation for multi-material)
            const matNames = matMapping.split('|')
            const mats = matNames
              .map((name) => getThreeMaterial(name))
              .filter(Boolean) as THREE.Material[]
            if (mats.length > 0) {
              child.material = mats
            }
          } else {
            // Single material mesh - check for color variation
            const matData = getMaterial(matMapping)
            const variation = matData?.colorVariation ?? 0

            if (variation > 0) {
              const meshName = child.name || child.uuid
              const clone = createVariedClone(matMapping, meshName)
              if (clone) {
                child.material = clone
              }
            } else {
              const baseMat = getThreeMaterial(matMapping)
              if (baseMat) {
                child.material = baseMat
              }
            }
          }
        }
      })

      console.log('Materials applied from store')
    }

    loadAndApplyMaterials()
  }, [
    scene,
    initializeMaterials,
    getThreeMaterial,
    getMaterial,
    createVariedClone,
    loadDefaultMaterials,
  ])

  // Reactive re-assignment when colorVariation changes on any material.
  // The `materials` map reference changes on every updateMaterial call.
  useEffect(() => {
    const map = meshMaterialMapRef.current
    if (map.size === 0) return

    scene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return

      const matMapping = map.get(child.uuid)
      if (!matMapping || matMapping.includes('|')) return
      if (matMapping === 'Wires') return // Managed by useEnergyFlowAnimation

      const matData = useMaterialStore.getState().getMaterial(matMapping)
      const variation = matData?.colorVariation ?? 0
      const meshName = child.name || child.uuid

      if (variation > 0) {
        // Ensure clone exists (createVariedClone is idempotent if clone already tracked)
        let clone = useMaterialStore.getState().getVariedClone(matMapping, meshName)
        if (!clone) {
          clone = useMaterialStore.getState().createVariedClone(matMapping, meshName)
        }
        if (clone) {
          child.material = clone
        }
      } else {
        // Revert to shared base material
        const baseMat = useMaterialStore.getState().getThreeMaterial(matMapping)
        if (baseMat) {
          child.material = baseMat
        }
      }
    })
  }, [materials, scene])

  // Cleanup variation clones on unmount
  useEffect(() => {
    return () => {
      const store = useMaterialStore.getState()
      store.variationClones.forEach((_, matName) => {
        store.clearVariationClones(matName)
      })
    }
  }, [])

  // Configure directional light target and handle shadow map resize
  useEffect(() => {
    if (directionalLightRef.current && shadowType === 'directional') {
      const light = directionalLightRef.current

      // Set the light target to scene center for proper shadow alignment
      light.target.position.set(sceneCenter.x, sceneCenter.y, sceneCenter.z)
      light.target.updateMatrixWorld()

      // Dispose old shadow map when size changes (required for WebGL texture reallocation)
      if (light.shadow.map) {
        light.shadow.map.dispose()
        light.shadow.map = null
      }

      // Update shadow camera projection
      light.shadow.camera.updateProjectionMatrix()
      light.shadow.needsUpdate = true
    }
  }, [shadowType, shadowMapSize, shadowCameraSize])

  // Get the WebGL renderer to check shadow map type
  const { gl } = useThree()

  // Log renderer shadow map settings on mount
  useEffect(() => {
    console.log('Renderer shadowMap enabled:', gl.shadowMap.enabled)
    console.log('Renderer shadowMap type:', gl.shadowMap.type)
    console.log('VSMShadowMap value:', THREE.VSMShadowMap)
    console.log('Using VSM:', gl.shadowMap.type === THREE.VSMShadowMap)
  }, [gl])

  // Apply shadow blur for VSM shadows
  useEffect(() => {
    if (directionalLightRef.current) {
      const shadow = directionalLightRef.current.shadow
      shadow.radius = shadowRadius
      shadow.blurSamples = Math.max(4, Math.floor(shadowRadius * 2)) // VSM blur samples
      console.log('Shadow radius:', shadowRadius, 'blurSamples:', shadow.blurSamples)
    }
  }, [shadowRadius])

  // Safety timeout - complete loading after 8s even if scene hasn't fully rendered
  // This prevents the loading screen from getting stuck at 95% if WebGL/rendering fails
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (!hasSignaledLoadRef.current) {
        console.warn('Loading timeout reached - forcing completion')
        hasSignaledLoadRef.current = true
        setLoaded()
      }
    }, 8000)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [setLoaded])

  // Track frame rendering for loading progress
  // Wait for several frames to ensure scene is fully rendered and stable
  useFrame(() => {
    if (hasSignaledLoadRef.current || isLoaded) return

    frameCountRef.current++

    // Smooth progress simulation - scene loads fast but we want smooth UX
    // First 5 frames: rapid initial progress (model parsing)
    // Frames 5-15: material application
    // Frames 15-25: shadow compilation
    // Frames 25-35: final stabilization
    const targetFrames = 35
    const progress = Math.min((frameCountRef.current / targetFrames) * 100, 100)

    // Use eased progress for smoother feeling
    const easedProgress = easeOutExpo(progress / 100) * 100
    setProgress(easedProgress)

    if (frameCountRef.current >= targetFrames) {
      hasSignaledLoadRef.current = true
      // Clear the fallback timeout since we loaded successfully
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setLoaded()
    }
  })

  // Easing function for smooth progress
  function easeOutExpo(x: number): number {
    return x === 1 ? 1 : 1 - Math.pow(2, -10 * x)
  }

  // Note: Text meshes have castShadow=false which prevents shadows in directional mode.
  // ContactShadows mode will show text shadows as it ignores castShadow property.

  return (
    <>
      {/* Camera Controls with Constraints */}
      <CameraController />

      {/* POI Markers & Colliders */}
      <POIMarkers />
      <POIColliders />

      {/* Decal System */}
      <Decals />
      <DecalPlacer />

      {/* Lighting */}
      <ambientLight intensity={ambientIntensity} />
      <directionalLight
        ref={directionalLightRef}
        position={getDirectionalLightPosition()}
        intensity={directionalIntensity}
        castShadow={shadowType === 'directional'}
        shadow-intensity={shadowOpacity}
        shadow-mapSize-width={shadowMapSize}
        shadow-mapSize-height={shadowMapSize}
        shadow-bias={shadowBias}
        shadow-normalBias={shadowNormalBias}
        shadow-radius={shadowRadius}
        shadow-camera-left={-shadowCameraSize / 2}
        shadow-camera-right={shadowCameraSize / 2}
        shadow-camera-top={shadowCameraSize / 2}
        shadow-camera-bottom={-shadowCameraSize / 2}
        shadow-camera-near={1}
        shadow-camera-far={shadowCameraSize * 4}
      >
        {/* Explicit target for proper shadow alignment */}
        <object3D position={[sceneCenter.x, sceneCenter.y, sceneCenter.z]} />
      </directionalLight>
      <hemisphereLight args={[hemisphereSkyColor, hemisphereGroundColor, hemisphereIntensity]} />

      {/* The GLB model */}
      <primitive object={scene} />

      {/* Shadow System - Toggle between contact shadows and directional shadow mapping */}
      {shadowType === 'contact' ? (
        <ContactShadows
          key={shadowResolution}
          position={[0, 0, 0]}
          opacity={shadowOpacity}
          scale={100}
          blur={shadowBlur}
          far={100}
          resolution={shadowResolution}
          color="#000000"
        />
      ) : (
        /* Ground plane for receiving directional shadows */
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
          <planeGeometry args={[500, 500]} />
          <shadowMaterial opacity={shadowOpacity} />
        </mesh>
      )}
    </>
  )
}

// Preload the model
useGLTF.preload('/models/Blockout.glb', true)
